// Lógica PURA de la pantalla Cobranza: qué le pide cada pastilla al endpoint, qué recorta el
// navegador, cómo se ordena y cuántos días de mora lleva una cuota. Sin React y sin fetch.
import { LoanInstallmentStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanInstallmentStatusEnum';
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { CREDIT_ID_PATTERN } from '@fiado/type-kit/bin/common/constants/CreditIdPattern';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import type { LoanCreditDeviceBlock, LoanInstallmentListQuery } from '@/api/services/loan-credit';
import { wireEnum } from '@/lib/wireEnum';
import { IMEI_PATTERN } from '@/lib/imei';
import { dayOffset, daysBetweenDays, mxDay } from '@/lib/dateHelpers';
import { lastMessageSortValueOf } from './lastMessage';
import { lockScheduleOf, type CreditTone } from '../creditFile/logic';
import { lockFilterBucketOf, lockPhaseOf } from '../lockPhase';
import type {
    CollectionPriority,
    DelinquencyBandKey,
    DeviceLockState,
    InstallmentArrearsFilter,
    InstallmentSearch,
    InstallmentCore,
    InstallmentFilters,
    InstallmentLevelFilter,
    InstallmentMdmKey,
    InstallmentRow,
    InstallmentSelection,
    InstallmentSort,
    InstallmentSortKey,
    InstallmentStatKey,
    SortDirection,
} from './types';

/** El día de hoy en el piso de venta. Es el único criterio de día del repo. */
function todayMx(): string {
    return mxDay(new Date().toISOString());
}

/** El más tardío de dos días; la cadena vacía es «sin piso». */
function latestDay(a: string, b: string): string {
    if (!a) return b;
    if (!b) return a;
    return a > b ? a : b;
}

/** El más temprano de dos días; la cadena vacía es «sin techo». */
function earliestDay(a: string, b: string): string {
    if (!a) return b;
    if (!b) return a;
    return a < b ? a : b;
}

export interface InstallmentStatDefinition {
    key: InstallmentStatKey;
    label: string;
    /** Color del número de la pastilla. */
    tone: CreditTone;
}

/** Las pastillas del mock, en su orden. */
export const INSTALLMENT_STATS: readonly InstallmentStatDefinition[] = [
    { key: 'all', label: 'Todos', tone: 'neutral' },
    { key: 'upcoming', label: 'Pendiente', tone: 'info' },
    { key: 'overdue', label: 'Vencido', tone: 'danger' },
    { key: 'withoutMdm', label: 'Sin MDM', tone: 'danger' },
    { key: 'withMdm', label: 'Con MDM', tone: 'success' },
];

/** El corte propio de un stream, antes de cruzarlo con el del filtro. */
interface ListStream {
    status: LoanInstallmentStatusEnum;
    dueFrom?: string;
    dueTo?: string;
    /** La saldada no acumula mora: el tramo no se traduce a su vencimiento. */
    settled?: boolean;
    /** El flag `OVERDUE` ya implica mora: «sin mora» no le pide nada. */
    overdueFlag?: boolean;
}

/**
 * Los cuatro estados del listado, siempre los mismos: así las cinco pastillas cuentan y recortan
 * sobre las MISMAS cuotas. `OVERDUE` es el flag del barrido de la noche, y la `PENDING` con
 * vencimiento anterior a hoy todavía no lo tiene.
 */
const LIST_STREAMS: readonly ListStream[] = [
    { status: LoanInstallmentStatusEnum.OVERDUE, overdueFlag: true },
    { status: LoanInstallmentStatusEnum.PENDING },
    { status: LoanInstallmentStatusEnum.PAID, settled: true },
    { status: LoanInstallmentStatusEnum.PAID_IN_ADVANCE, settled: true },
];

/** El rango de vencimiento del panel. */
export type InstallmentDueRange = Pick<InstallmentFilters, 'fromDay' | 'toDay'>;

/** Lo del filtro que viaja al servidor: el rango de vencimiento y el tramo de mora. */
export type InstallmentServerCut = InstallmentDueRange & Pick<InstallmentFilters, 'arrears'>;

/** El vencimiento que da ese tramo de mora hoy; la cadena vacía es «sin límite». */
export function arrearsDueRange(
    arrears: InstallmentArrearsFilter,
    today: string = todayMx(),
): InstallmentDueRange {
    const band = DELINQUENCY_SCALE.find((item) => item.key === arrears);
    if (!band) return { fromDay: '', toDay: '' };
    return {
        fromDay: band.maxDays === null ? '' : dayOffset(today, -band.maxDays),
        toDay: band.minDays === 0 ? '' : dayOffset(today, -band.minDays),
    };
}

/**
 * Lo que la pantalla le pide al endpoint, cruzando cada stream con las fechas y la mora del panel.
 * El stream cuyo cruce queda invertido NO se emite: sería una consulta que no puede traer nada.
 */
export function installmentListQueries(
    cut: InstallmentServerCut,
    today: string = todayMx(),
): readonly LoanInstallmentListQuery[] {
    const arrears = arrearsDueRange(cut.arrears, today);
    const queries: LoanInstallmentListQuery[] = [];
    for (const stream of LIST_STREAMS) {
        if (stream.overdueFlag && cut.arrears === 'none') continue;
        let arrearsFrom = arrears.fromDay;
        let arrearsTo = arrears.toDay;
        if (stream.settled) {
            // Una saldada tiene mora nula: solo «sin mora» (o ningún tramo) la deja pasar.
            if (cut.arrears !== 'all' && cut.arrears !== 'none') continue;
            arrearsFrom = '';
            arrearsTo = '';
        }
        const dueFrom = latestDay(latestDay(cut.fromDay, arrearsFrom), stream.dueFrom ?? '');
        const dueTo = earliestDay(earliestDay(cut.toDay, arrearsTo), stream.dueTo ?? '');
        if (dueFrom && dueTo && dueFrom > dueTo) continue;
        queries.push({
            status: stream.status,
            ...(dueFrom ? { dueFrom } : {}),
            ...(dueTo ? { dueTo } : {}),
        });
    }
    return queries;
}

/** Clasifica la búsqueda. Lo que no es ni ULID ni IMEI no se manda. */
export function parseInstallmentSearch(text: string): InstallmentSearch {
    const value = text.trim();
    if (value === '') return { kind: 'none' };
    if (IMEI_PATTERN.test(value)) return { kind: 'imei', imei: value };
    const upper = value.toUpperCase();
    if (CREDIT_ID_PATTERN.test(upper)) return { kind: 'creditId', creditId: upper };
    return { kind: 'invalid' };
}

/** La consulta de la búsqueda: el plan de UN crédito, con el rango de vencimiento si lo hay. */
export function installmentSearchQuery(
    search: Extract<InstallmentSearch, { kind: 'creditId' | 'imei' }>,
    range: InstallmentDueRange,
): LoanInstallmentListQuery {
    return {
        ...(search.kind === 'creditId' ? { creditId: search.creditId } : { imei: search.imei }),
        ...(range.fromDay ? { dueFrom: range.fromDay } : {}),
        ...(range.toDay ? { dueTo: range.toDay } : {}),
    };
}

/**
 * El rango de los atajos Hoy/7d/30d/Trim/Todo. La ventana abarca los dos lados de hoy: el listado
 * trae lo vencido y lo que está por vencer a la vez, y la pastilla recorta después.
 */
export function dueShortcutRange(days: number, today: string = todayMx()): InstallmentDueRange {
    if (days < 0) return { fromDay: '', toDay: '' };
    return { fromDay: dayOffset(today, -days), toDay: dayOffset(today, days) };
}

/** La cuota ya saldada no acumula mora ni se ofrece para cobrar: su deuda dejó de correr. */
export const SETTLED_STATUSES: readonly LoanInstallmentStatusEnum[] = [
    LoanInstallmentStatusEnum.PAID,
    LoanInstallmentStatusEnum.PAID_IN_ADVANCE,
    LoanInstallmentStatusEnum.RESTRUCTURED,
];

/**
 * Días de mora de una cuota, derivados de su vencimiento contra hoy: el listado no los publica.
 * `null` es «no hay mora que medir» —sin fecha o ya saldada— y NUNCA es cero.
 */
export function installmentArrearsDays(
    row: InstallmentCore,
    today: string = todayMx(),
): number | null {
    if (!row.dueDate) return null;
    if (SETTLED_STATUSES.includes(row.status)) return null;
    return Math.max(0, daysBetweenDays(today, row.dueDate));
}

export type { DelinquencyBandKey };

export interface DelinquencyBand {
    key: DelinquencyBandKey;
    label: string;
    minDays: number;
    /** `null` = sin techo: el último tramo no cierra. */
    maxDays: number | null;
    tone: CreditTone;
}

/**
 * Escala de severidad de la mora, PROVISIONAL: los cortes definitivos todavía no están elegidos y
 * estos son los del mock. Vive sólo aquí; nadie más define tramos de mora en esta pantalla.
 */
export const DELINQUENCY_SCALE: readonly DelinquencyBand[] = [
    { key: 'none', label: 'Sin mora', minDays: 0, maxDays: 0, tone: 'success' },
    { key: 'early', label: '1 a 7 días', minDays: 1, maxDays: 7, tone: 'warning' },
    { key: 'mid', label: '8 a 30 días', minDays: 8, maxDays: 30, tone: 'warning' },
    { key: 'late', label: '31 a 60 días', minDays: 31, maxDays: 60, tone: 'danger' },
    { key: 'critical', label: '61 días o más', minDays: 61, maxDays: null, tone: 'danger' },
];

/** El tramo en el que cae una mora. `null` entra y `null` sale: no se sabe no es sin mora. */
export function delinquencyBandOf(arrearsDays: number): DelinquencyBand;
export function delinquencyBandOf(arrearsDays: number | null): DelinquencyBand | null;
export function delinquencyBandOf(arrearsDays: number | null): DelinquencyBand | null {
    if (arrearsDays === null) return null;
    // La cuota que todavía no vence cae en el primer tramo: sin mora, que es lo que es.
    const days = Math.max(0, arrearsDays);
    return (
        DELINQUENCY_SCALE.find(
            (band) => days >= band.minDays && (band.maxDays === null || days <= band.maxDays),
        ) ?? DELINQUENCY_SCALE[0]
    );
}

/** El estado del equipo que pintan la columna y los filtros; `LOCKED` con hora futura es programado. */
export function deviceLockStateOf(
    device: LoanCreditDeviceBlock,
    nowMs: number = Date.now(),
): DeviceLockState {
    switch (device.status) {
        case 'NOT_ENROLLED':
            return 'noDevice';
        case 'UNREADABLE':
            return 'unreadable';
        case 'NO_PERMISSION':
            return 'noPermission';
        case 'RESOLVED':
            if (
                device.mdmStatus === undefined ||
                wireEnum<LoanDeviceMdmStatusEnum>(device.mdmStatus) !==
                    LoanDeviceMdmStatusEnum.LOCKED
            ) {
                return 'unlocked';
            }
            return lockScheduleOf(device, nowMs).scheduled ? 'scheduled' : 'locked';
    }
}

/** Prioridad de cobro por cuota: 1 es la más urgente. */
export const COLLECTION_PRIORITY = {
    /** Vencida: solo el candado confirmado baja a 2; el resto cuenta como no bloqueado. */
    overdueByDevice: {
        unlocked: 1,
        scheduled: 1,
        noDevice: 1,
        unreadable: 1,
        noPermission: 1,
        locked: 2,
    } satisfies Record<DeviceLockState, CollectionPriority>,
    dueSoon: 3,
    dueLater: 4,
    settled: 5,
} as const;

/** «Por vencer pronto» alcanza hasta este número de días desde hoy, inclusive. */
export const DUE_SOON_DAYS = 7;

/** Para la prioridad, un candado sin confirmar o desmentido cuenta como sin bloqueo. */
function priorityLockStateOf(device: LoanCreditDeviceBlock, nowMs: number): DeviceLockState {
    const state = deviceLockStateOf(device, nowMs);
    if (state !== 'locked') return state;
    return lockPhaseOf(device, new Date(nowMs)) === 'locked' ? 'locked' : 'unlocked';
}

/** La prioridad de cobro de una cuota contra hoy. */
export function installmentPriorityOf(
    row: InstallmentRow,
    today: string = todayMx(),
    nowMs: number = Date.now(),
): CollectionPriority {
    if (SETTLED_STATUSES.includes(row.status)) return COLLECTION_PRIORITY.settled;
    const overdue =
        row.status === LoanInstallmentStatusEnum.OVERDUE ||
        (row.dueDate !== undefined && row.dueDate < today);
    if (overdue) return COLLECTION_PRIORITY.overdueByDevice[priorityLockStateOf(row.device, nowMs)];
    if (row.dueDate === undefined) return COLLECTION_PRIORITY.dueLater;
    return daysBetweenDays(row.dueDate, today) <= DUE_SOON_DAYS
        ? COLLECTION_PRIORITY.dueSoon
        : COLLECTION_PRIORITY.dueLater;
}

/** El chip de Bloqueo mira la misma lectura que la celda; «Sin bloq» incluye el programado. */
function matchesLocks(row: InstallmentRow, filters: InstallmentFilters, nowMs: number): boolean {
    if (filters.locks.length === 0) return true;
    const device = row.device;
    const bucket =
        device.status === 'RESOLVED' ? lockFilterBucketOf(device, new Date(nowMs)) : null;
    return filters.locks.some((lock) =>
        lock === 'noDevice' ? device.status === 'NOT_ENROLLED' : bucket === lock,
    );
}

/** Si el crédito tiene equipo en MDM; `null` si no se pudo leer, y no cae en ninguna pastilla. */
export function installmentMdmOf(
    row: InstallmentRow,
    nowMs: number = Date.now(),
): InstallmentMdmKey | null {
    const state = deviceLockStateOf(row.device, nowMs);
    if (state === 'noDevice') return 'withoutMdm';
    if (state === 'unreadable' || state === 'noPermission') return null;
    return 'withMdm';
}

function isOverdueRow(row: InstallmentCore, today: string): boolean {
    if (row.status === LoanInstallmentStatusEnum.OVERDUE) return true;
    return (
        row.status === LoanInstallmentStatusEnum.PENDING &&
        row.dueDate !== undefined &&
        row.dueDate < today
    );
}

function isUpcomingRow(row: InstallmentCore, today: string): boolean {
    return (
        row.status === LoanInstallmentStatusEnum.PENDING &&
        row.dueDate !== undefined &&
        row.dueDate >= today
    );
}

/** Lo que recorta la pastilla elegida sobre las cuotas cargadas; «Todos» no recorta nada. */
export function filterByStat(
    rows: readonly InstallmentRow[],
    stat: InstallmentStatKey,
    today: string = todayMx(),
    nowMs: number = Date.now(),
): readonly InstallmentRow[] {
    switch (stat) {
        case 'all':
            return rows;
        case 'upcoming':
            return rows.filter((row) => isUpcomingRow(row, today));
        case 'overdue':
            return rows.filter((row) => isOverdueRow(row, today));
        default:
            return rows.filter((row) => installmentMdmOf(row, nowMs) === stat);
    }
}

/** El número de cada pastilla sobre las cuotas cargadas. */
export type InstallmentStatCounts = Record<InstallmentStatKey, number>;

/** Las cinco pastillas cuentan las MISMAS filas: elegir una no cambia lo que se cargó. */
export function installmentStatCounts(
    rows: readonly InstallmentRow[],
    today: string = todayMx(),
    nowMs: number = Date.now(),
): InstallmentStatCounts {
    const count = (predicate: (row: InstallmentRow) => boolean) => rows.filter(predicate).length;
    return {
        all: rows.length,
        upcoming: count((row) => isUpcomingRow(row, today)),
        overdue: count((row) => isOverdueRow(row, today)),
        withoutMdm: count((row) => installmentMdmOf(row, nowMs) === 'withoutMdm'),
        withMdm: count((row) => installmentMdmOf(row, nowMs) === 'withMdm'),
    };
}

/** El tramo de la mora de hoy; como el mock, la mora que no se mide cuenta como «sin mora». */
function matchesArrears(row: InstallmentRow, arrears: InstallmentArrearsFilter, today: string) {
    if (arrears === 'all') return true;
    return delinquencyBandOf(installmentArrearsDays(row, today) ?? 0).key === arrears;
}

/** El nivel congelado al originar debe ser uno de los chips prendidos. */
function matchesLevels(row: InstallmentRow, levels: readonly InstallmentLevelFilter[]): boolean {
    if (levels.length === 0) return true;
    const level = originationLevelOf(row);
    return level !== undefined && levels.some((item) => item === level);
}

/** Un rango `null`-abierto en centavos; con algún tope puesto, el valor ausente no pasa. */
function withinCents(value: number | null, min: number | null, max: number | null): boolean {
    if (min === null && max === null) return true;
    if (value === null) return false;
    return (min === null || value >= min) && (max === null || value <= max);
}

/**
 * Lo que el navegador recorta sobre las cuotas ya cargadas. El vencimiento y la búsqueda NO se
 * filtran aquí: van al servidor, y repetirlos borraría la cuota que llegó sin fecha.
 */
export function filterInstallments(
    rows: readonly InstallmentRow[],
    filters: InstallmentFilters,
    nowMs: number = Date.now(),
    today: string = todayMx(),
): InstallmentRow[] {
    return rows.filter((row) => {
        if (!withinCents(row.totalCents, filters.amountMinCents, filters.amountMaxCents))
            return false;
        const balance = row.credit?.outstandingBalanceCents ?? null;
        if (!withinCents(balance, filters.balanceMinCents, filters.balanceMaxCents)) return false;
        if (!matchesLevels(row, filters.levels)) return false;
        if (!matchesArrears(row, filters.arrears, today)) return false;
        return matchesLocks(row, filters, nowMs);
    });
}

/** Orden de los estados de cuota: primero lo que exige cobrar, al final lo que ya no aplica. */
const STATUS_ORDER: Record<LoanInstallmentStatusEnum, number> = {
    [LoanInstallmentStatusEnum.OVERDUE]: 0,
    [LoanInstallmentStatusEnum.PENDING]: 1,
    [LoanInstallmentStatusEnum.PAID_IN_ADVANCE]: 2,
    [LoanInstallmentStatusEnum.PAID]: 3,
    [LoanInstallmentStatusEnum.RESTRUCTURED]: 4,
};

/** Primero lo bloqueado; al final lo que no se sabe. */
const LOCK_ORDER: Record<DeviceLockState, number> = {
    locked: 0,
    scheduled: 1,
    unlocked: 2,
    noDevice: 3,
    unreadable: 4,
    noPermission: 5,
};

/** Los numéricos arrancan de mayor a menor; el vencimiento, de la cuota más vieja a la más nueva. */
const DEFAULT_DIRECTION: Record<InstallmentSortKey, SortDirection> = {
    due: 'asc',
    credit: 'asc',
    number: 'asc',
    level: 'asc',
    customer: 'asc',
    amount: 'desc',
    coupons: 'asc',
    balance: 'desc',
    status: 'asc',
    arrears: 'desc',
    lock: 'asc',
    lastMessage: 'asc',
    priority: 'asc',
};

/** El orden con el que abre la pantalla, y al que vuelve el tercer clic sobre una columna. */
export const DEFAULT_INSTALLMENT_SORT: InstallmentSort = { key: 'priority', direction: 'asc' };

/** Clic en un encabezado: estrena su default, luego lo invierte y al tercero vuelve a la prioridad. */
export function nextInstallmentSort(
    current: InstallmentSort,
    key: InstallmentSortKey,
): InstallmentSort {
    if (current.key !== key) return { key, direction: DEFAULT_DIRECTION[key] };
    if (key !== 'priority' && current.direction !== DEFAULT_DIRECTION[key]) {
        return DEFAULT_INSTALLMENT_SORT;
    }
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

function compareBy(
    key: InstallmentSortKey,
    a: InstallmentRow,
    b: InstallmentRow,
    today: string,
): number {
    switch (key) {
        case 'due':
            return (a.dueDate ?? '').localeCompare(b.dueDate ?? '');
        case 'credit':
            return a.creditId.localeCompare(b.creditId, 'es-MX');
        case 'number':
            return a.installmentNumber - b.installmentNumber;
        case 'amount':
            return a.totalCents - b.totalCents;
        case 'balance':
            return a.outstandingBalanceCents - b.outstandingBalanceCents;
        case 'status':
            return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
        case 'arrears':
            return (
                (installmentArrearsDays(a, today) ?? 0) - (installmentArrearsDays(b, today) ?? 0)
            );
        case 'coupons':
            return (couponsProgressOf(a) ?? 0) - (couponsProgressOf(b) ?? 0);
        case 'priority':
            return installmentPriorityOf(a, today) - installmentPriorityOf(b, today);
        case 'lock':
            return (
                LOCK_ORDER[deviceLockStateOf(a.device)] - LOCK_ORDER[deviceLockStateOf(b.device)]
            );
        case 'customer':
            return (customerNameOf(a) ?? '').localeCompare(customerNameOf(b) ?? '', 'es-MX');
        case 'level':
            return (levelRankOf(a) ?? 0) - (levelRankOf(b) ?? 0);
        case 'lastMessage':
            return (
                (lastMessageSortValueOf(a, today) ?? 0) - (lastMessageSortValueOf(b, today) ?? 0)
            );
    }
}

/** El nivel congelado al originar; `undefined` sin crédito o si se originó sin SCI. */
export function originationLevelOf(row: InstallmentRow): ClientLevelEnum | undefined {
    const level = row.credit?.clientLevelAtOrigination;
    return level === undefined ? undefined : wireEnum<ClientLevelEnum>(level);
}

/** Jerarquía del nivel: el orden del negocio, no el alfabético. */
const LEVEL_ORDER: Record<ClientLevelEnum, number> = {
    [ClientLevelEnum.GOLD]: 0,
    [ClientLevelEnum.SILVER]: 1,
    [ClientLevelEnum.BRONZE]: 2,
    [ClientLevelEnum.NOT_ELIGIBLE]: 3,
};

function levelRankOf(row: InstallmentRow): number | null {
    const level = originationLevelOf(row);
    return level === undefined ? null : LEVEL_ORDER[level];
}

/** El nombre del cliente cuando la lectura lo trajo; `null` si falta por cualquier motivo. */
function customerNameOf(row: InstallmentRow): string | null {
    const { customerName, customerNameStatus } = row.customer;
    return customerNameStatus === 'OK' && customerName !== null && customerName.trim() !== ''
        ? customerName
        : null;
}

/** Fracción del plan cubierta, de 0 a 1; `null` sin crédito o sin plazo. */
export function couponsProgressOf(row: InstallmentRow): number | null {
    if (row.credit === null || row.credit.termWeeks <= 0) return null;
    return Math.min(1, row.credit.paidInstallments / row.credit.termWeeks);
}

/** Lo ausente al final; `null` cuando los dos valen y hay que comparar de verdad. */
function compareMissing(a: string | undefined, b: string | undefined): number | null {
    if (a === b) return a === undefined ? 0 : null;
    if (a === undefined) return 1;
    if (b === undefined) return -1;
    return null;
}

/** Desempate de la prioridad: vencimiento ascendente, lo ausente al final, y luego la identidad. */
function compareByDueThenIdentity(a: InstallmentRow, b: InstallmentRow): number {
    const missing = compareMissing(a.dueDate, b.dueDate);
    if (missing !== null && missing !== 0) return missing;
    const due = (a.dueDate ?? '').localeCompare(b.dueDate ?? '');
    if (due !== 0) return due;
    return installmentKey(a).localeCompare(installmentKey(b));
}

/** Como `compareMissing`, para números: el `null` va al final en las dos direcciones. */
function compareNullLast(a: number | null, b: number | null): number | null {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return null;
}

export function sortInstallments(
    rows: readonly InstallmentRow[],
    sort: InstallmentSort,
    today: string = todayMx(),
): InstallmentRow[] {
    const factor = sort.direction === 'asc' ? 1 : -1;
    // La cuota sin fecha queda al final SIEMPRE: invertir el factor la subiría a la cabeza del
    // descendente, y «no se sabe cuándo vence» no es la cuota más vencida.
    return [...rows].sort((a, b) => {
        if (sort.key === 'due') {
            const missing = compareMissing(a.dueDate, b.dueDate);
            if (missing !== null) return missing;
        }
        if (sort.key === 'customer') {
            const missing = compareMissing(
                customerNameOf(a) ?? undefined,
                customerNameOf(b) ?? undefined,
            );
            if (missing !== null) return missing;
        }
        if (sort.key === 'level') {
            const missing = compareNullLast(levelRankOf(a), levelRankOf(b));
            if (missing !== null) return missing;
        }
        if (sort.key === 'coupons') {
            const missing = compareNullLast(couponsProgressOf(a), couponsProgressOf(b));
            if (missing !== null) return missing;
        }
        if (sort.key === 'lastMessage') {
            const missing = compareNullLast(
                lastMessageSortValueOf(a, today),
                lastMessageSortValueOf(b, today),
            );
            if (missing !== null) return missing;
        }
        const primary = factor * compareBy(sort.key, a, b, today);
        if (primary !== 0 || sort.key !== 'priority') return primary;
        return compareByDueThenIdentity(a, b);
    });
}

/** Cuántos filtros hay puestos, para el contador del panel. */
export function activeFilterCount(filters: InstallmentFilters): number {
    let total = 0;
    if (filters.search.trim()) total += 1;
    if (filters.amountMinCents !== null) total += 1;
    if (filters.amountMaxCents !== null) total += 1;
    if (filters.balanceMinCents !== null) total += 1;
    if (filters.balanceMaxCents !== null) total += 1;
    if (filters.fromDay) total += 1;
    if (filters.toDay) total += 1;
    total += filters.levels.length + filters.locks.length;
    if (filters.arrears !== 'all') total += 1;
    return total;
}

/** El rango de vencimiento quedó al revés: la fecha inicial es posterior a la final. */
export function isInvertedDueRange(range: InstallmentDueRange): boolean {
    return range.fromDay !== '' && range.toDay !== '' && range.fromDay > range.toDay;
}

export const INVERTED_RANGE_MESSAGE = 'La fecha inicial es posterior a la final';

export function activeFiltersLabel(count: number): string {
    return `${String(count)} ${count === 1 ? 'filtro' : 'filtros'}`;
}

/** Identidad de una cuota: el crédito solo no alcanza, dos filas suyas serían la misma. */
export function installmentKey(selection: InstallmentSelection): string {
    return `${selection.creditId}#${String(selection.installmentNumber)}`;
}
